import {
  upsertPupilLessonProgressArgsSchema,
  type UpsertPupilLessonProgressArgs,
} from "@oaknational/google-classroom-addon/types";

import type { ClassroomProgressContext } from "./classroomAssignmentContext";

import type { LessonSectionResults } from "@/context/PupilLessonProgress";
import type { QuestionState } from "@/components/PupilComponents/QuizUtils/questionTypes";

type QuizSectionResult = NonNullable<
  LessonSectionResults["starter-quiz"] | LessonSectionResults["exit-quiz"]
>;

const sanitizeCorrectAnswer = (
  correctAnswer: QuestionState["correctAnswer"],
) => {
  if (typeof correctAnswer === "string") return correctAnswer;
  if (Array.isArray(correctAnswer)) {
    return correctAnswer.filter(
      (answer): answer is string => typeof answer === "string",
    );
  }
  return undefined;
};

const mapQuizQuestionResults = (questionResults: QuestionState[] | undefined) =>
  questionResults?.map((qr) => ({
    mode: qr.mode,
    grade: qr.grade,
    pupilAnswer: qr.pupilAnswer ?? null,
    feedback: qr.feedback,
    isPartiallyCorrect: qr.isPartiallyCorrect,
    usedHint: qr.offerHint,
    correctAnswer: sanitizeCorrectAnswer(qr.correctAnswer),
  }));

const mapQuizProgress = (quizResult: QuizSectionResult | undefined) => {
  if (!quizResult) return undefined;
  return {
    grade: quizResult.grade ?? 0,
    numQuestions: quizResult.numQuestions ?? 0,
    isComplete: quizResult.isComplete ?? false,
    questionResults: mapQuizQuestionResults(quizResult.questionResults),
  };
};

/**
 * Maps the pupil lesson progress store's section results into the payload the
 * Google Classroom add-on expects, so pupil work is written back to the
 * assignment submission.
 */
export function mapToSubmitPupilProgress(
  context: ClassroomProgressContext,
  sectionResults: LessonSectionResults,
): UpsertPupilLessonProgressArgs {
  const starterQuiz = mapQuizProgress(sectionResults["starter-quiz"]);
  const exitQuiz = mapQuizProgress(sectionResults["exit-quiz"]);

  const payload = {
    submissionId: context.submissionId,
    attachmentId: context.attachmentId,
    courseId: context.courseId,
    itemId: context.itemId,
    pupilLoginHint: context.pupilLoginHint,
    ...(starterQuiz && {
      starterQuiz,
    }),
    ...(exitQuiz && {
      exitQuiz,
    }),
    ...(sectionResults["video"] && {
      video: {
        played: sectionResults["video"].played,
        duration: sectionResults["video"].duration,
        timeElapsed: sectionResults["video"].timeElapsed,
        isComplete: sectionResults["video"].isComplete,
      },
    }),
    ...(sectionResults["intro"] && {
      intro: {
        worksheetAvailable: sectionResults["intro"].worksheetAvailable,
        worksheetDownloaded: sectionResults["intro"].worksheetDownloaded,
        isComplete: sectionResults["intro"].isComplete,
      },
    }),
  };

  return upsertPupilLessonProgressArgsSchema.parse(payload);
}
